import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { ChevronDown, Filter, RotateCcw, Search } from "lucide-react";
import { cn } from "../../../lib/utils";
import { normalizeServiceOrderStatus } from "../serviceOrderStatus";

interface ApiServiceOrder {
  id: string;
  serviceOrderNumber?: string;
  title: string;
  description: string;
  serviceOrderType: string;
  priority: string;
  status: string;
  customerName?: string | null;
  meterNumber?: string | null;
  engineerName?: string | null;
  address?: string | null;
  scheduledDate?: string | null;
  createdAt: string;
}

interface ServiceOrdersApiResponse {
  success: boolean;
  data?: {
    data?: ApiServiceOrder[];
  };
}

interface FieldEngineersApiResponse {
  success: boolean;
  data?: Array<{
    id: string;
    name: string;
    phone?: string;
  }>;
}

interface HierarchyApiResponse {
  success: boolean;
  data?: {
    customers?: Array<{
      id: string;
      name: string;
    }>;
  };
}

interface ServiceOrderView {
  id: string;
  serviceOrderNumber: string;
  title: string;
  type: string;
  priority: string;
  status: string;
  customerName: string;
  meterNumber: string;
  engineerName: string;
  scheduledDate?: string;
  createdAt: string;
}

const STATUS_FILTER_OPTIONS = [
  "pending",
  "assigned",
  "in_progress",
  "completed",
  "cancelled",
];

const TYPE_FILTER_OPTIONS = [
  { value: "customer_indexing", label: "Customer Registration" },
  { value: "customer_profiling", label: "Customer Profiling & Analysis" },
  { value: "meter_registration", label: "Meter Registration" },
  { value: "meter_installation", label: "Meter Installation" },
  { value: "meter_un_installation", label: "Meter Un Installation" },
  { value: "disconnection", label: "Service Disconnection" },
  { value: "inspection", label: "Routine Inspection" },
  { value: "maintenance", label: "Maintenance" },
  { value: "repair", label: "Repair" },
];

const PRIORITY_FILTER_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

const getTimeValue = (value?: string): number | null => {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const ServiceOrderList: React.FC = () => {
  const [orders, setOrders] = useState<ServiceOrderView[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [hasLoadFailed, setHasLoadFailed] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasMoreOrders, setHasMoreOrders] = useState<boolean>(false);
  const [engineerOptions, setEngineerOptions] = useState<
    Array<{ id: string; name: string; phone?: string }>
  >([]);
  const [customerOptions, setCustomerOptions] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [filterCustomer, setFilterCustomer] = useState<string>("all");
  const [filterEngineer, setFilterEngineer] = useState<string>("all");
  const [filterType, setFilterType] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterPriority, setFilterPriority] = useState<string>("all");
  const [filterFromDate, setFilterFromDate] = useState<string>("");
  const [filterToDate, setFilterToDate] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [moreFiltersOpen, setMoreFiltersOpen] = useState<boolean>(false);
  const [listDataLoaded, setListDataLoaded] = useState<boolean>(false);
  const hasAutoLoadedRef = useRef<boolean>(false);

  const getStatusColor = (status: string): string => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "assigned":
        return "bg-blue-100 text-blue-800";
      case "in_progress":
        return "bg-purple-100 text-purple-800";
      case "completed":
        return "bg-green-100 text-green-800";
      case "cancelled":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getPriorityColor = (priority: string): string => {
    switch (priority) {
      case "urgent":
        return "bg-red-100 text-red-800";
      case "high":
        return "bg-orange-100 text-orange-800";
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      case "low":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const fetchServiceOrders = useCallback(async (page: number, append: boolean) => {
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
      setHasLoadFailed(false);
    }
    try {
      const response = await fetch(
        `https://backend.voltreum.com/api/v2/service-orders?page=${page}&pageSize=20`
      );
      const result: ServiceOrdersApiResponse = await response.json();
      const responseOrders = result.data?.data;
      const apiOrders = Array.isArray(responseOrders) ? responseOrders : [];
      const mappedOrders = apiOrders.map((order) => ({
        id: order.id,
        serviceOrderNumber: order.serviceOrderNumber || "-",
        title: order.title,
        type: order.serviceOrderType || "",
        priority: order.priority?.toLowerCase?.() || "low",
        status: normalizeServiceOrderStatus(order.status || "pending"),
        customerName: order.customerName || "-",
        meterNumber: order.meterNumber || "-",
        engineerName: order.engineerName || "-",
        scheduledDate: order.scheduledDate || undefined,
        createdAt: order.createdAt,
      }));
      setOrders((prev) => (append ? [...prev, ...mappedOrders] : mappedOrders));
      setCurrentPage(page);
      setHasMoreOrders(mappedOrders.length === 20);
    } catch (error) {
      console.error("Failed to fetch service orders:", error);
      if (!append) {
        setOrders([]);
        setHasLoadFailed(true);
      }
    } finally {
      if (append) {
        setIsLoadingMore(false);
      } else {
        setIsLoading(false);
      }
    }
  }, []);

  const refreshEngineerFilterOptions = useCallback(async (): Promise<void> => {
    try {
      const response = await fetch(
        "https://backend.voltreum.com/api/v2/field-engineers?activeOnly=true"
      );
      const result: FieldEngineersApiResponse = await response.json();
      const engineers = Array.isArray(result?.data) ? result.data : [];
      setEngineerOptions(
        engineers
          .filter((engineer) => engineer.id && engineer.name)
          .map((engineer) => ({
            id: engineer.id,
            name: engineer.name,
            phone: engineer.phone,
          }))
      );
    } catch (error) {
      console.error("Failed to fetch field engineers:", error);
      setEngineerOptions([]);
    }
  }, []);

  const refreshCustomerFilterOptions = useCallback(async (): Promise<void> => {
    try {
      const response = await fetch("https://backend.voltreum.com/api/v2/hierarchy");
      const result: HierarchyApiResponse = await response.json();
      const responseCustomers = result.data?.customers;
      const customers = Array.isArray(responseCustomers) ? responseCustomers : [];
      setCustomerOptions(
        customers
          .filter((customer) => customer.id && customer.name)
          .map((customer) => ({ id: customer.id, name: customer.name }))
      );
    } catch (error) {
      console.error("Failed to fetch customers:", error);
      setCustomerOptions([]);
    }
  }, []);

  const handleRefresh = useCallback(async (): Promise<void> => {
    try {
      await Promise.all([
        fetchServiceOrders(1, false),
        refreshEngineerFilterOptions(),
        refreshCustomerFilterOptions(),
      ]);
    } finally {
      setListDataLoaded(true);
    }
  }, [fetchServiceOrders, refreshEngineerFilterOptions, refreshCustomerFilterOptions]);

  useEffect(() => {
    if (hasAutoLoadedRef.current) return;
    hasAutoLoadedRef.current = true;
    void handleRefresh();
  }, [handleRefresh]);

  const handleLoadMore = () => {
    if (isLoading || isLoadingMore || !hasMoreOrders) return;
    fetchServiceOrders(currentPage + 1, true);
  };

  const assignTechnician = (orderId: string, technicianId: string): void => {
    const technician = engineerOptions.find((tech) => tech.id === technicianId);
    if (!technician) return;

    setOrders((prev) =>
      prev.map((order) =>
        order.id === orderId
          ? {
              ...order,
              engineerName: technician.name,
              status: "assigned",
            }
          : order
      )
    );
  };

  const updateOrderStatus = (orderId: string, newStatus: string): void => {
    setOrders((prev) =>
      prev.map((order) =>
        order.id === orderId
          ? { ...order, status: newStatus }
          : order
      )
    );
  };

  const filteredOrders = useMemo(
    () =>
      orders.filter((order: ServiceOrderView) => {
        const matchesCustomer =
          filterCustomer === "all" || order.customerName === filterCustomer;
        const matchesEngineer =
          filterEngineer === "all" || order.engineerName === filterEngineer;
        const matchesType = filterType === "all" || order.type === filterType;
        const matchesStatus =
          filterStatus === "all" || order.status === filterStatus;
        const matchesPriority =
          filterPriority === "all" || order.priority === filterPriority;
        const matchesSearch =
          order.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.serviceOrderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.meterNumber.toLowerCase().includes(searchTerm.toLowerCase());
        const createdAtTime = getTimeValue(order.createdAt);
        const fromTime = getTimeValue(filterFromDate);
        const toTime = getTimeValue(filterToDate);
        const matchesFromDate =
          fromTime === null || (createdAtTime !== null && createdAtTime >= fromTime);
        const matchesToDate =
          toTime === null || (createdAtTime !== null && createdAtTime <= toTime);

        return (
          matchesCustomer &&
          matchesEngineer &&
          matchesType &&
          matchesStatus &&
          matchesPriority &&
          matchesSearch &&
          matchesFromDate &&
          matchesToDate
        );
      }),
    [
      filterCustomer,
      filterEngineer,
      filterFromDate,
      filterToDate,
      filterPriority,
      filterStatus,
      filterType,
      orders,
      searchTerm,
    ]
  );

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Service Orders</CardTitle>
          <CardDescription>
            Manage and track all service requests
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-col lg:flex-row gap-4 items-center">
            <div className="relative w-full lg:flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by SO number, title, customer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 w-full"
              />
            </div>

            <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
              <div className="flex-1 min-w-[140px] sm:flex-none sm:w-48">
                <Select value={filterCustomer} onValueChange={setFilterCustomer}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Customers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Customers</SelectItem>
                    {customerOptions.map((customer) => (
                      <SelectItem key={customer.id} value={customer.name}>
                        {customer.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex-1 min-w-[140px] sm:flex-none sm:w-40">
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    {STATUS_FILTER_OPTIONS.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status.replace(/_/g, " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant={moreFiltersOpen ? "default" : "outline"}
                  size="icon"
                  onClick={() => setMoreFiltersOpen((open) => !open)}
                  className="shrink-0"
                  aria-label="More filters"
                >
                  <Filter className="h-4 w-4" />
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => void handleRefresh()}
                  disabled={isLoading}
                  className="shrink-0"
                  aria-label="Refresh list"
                >
                  <RotateCcw className={cn("h-4 w-4", isLoading && "animate-spin")} />
                </Button>
              </div>
            </div>
          </div>

          {/* Expanded Filters */}
          {moreFiltersOpen && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-border/70 animate-in fade-in slide-in-from-top-2">
              <div className="space-y-1.5">
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider ml-1">Engineer</p>
                <Select value={filterEngineer} onValueChange={setFilterEngineer}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Engineers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Engineers</SelectItem>
                    {engineerOptions.map((engineer) => (
                      <SelectItem key={engineer.id} value={engineer.name}>
                        {engineer.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider ml-1">Type</p>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {TYPE_FILTER_OPTIONS.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider ml-1">Priority</p>
                <Select value={filterPriority} onValueChange={setFilterPriority}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Priorities" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priorities</SelectItem>
                    {PRIORITY_FILTER_OPTIONS.map((priority) => (
                      <SelectItem key={priority.value} value={priority.value}>
                        {priority.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider ml-1">From</p>
                  <Input
                    type="date"
                    value={filterFromDate.split("T")[0]}
                    onChange={(e) => setFilterFromDate(e.target.value)}
                    className="w-full h-9"
                  />
                </div>
                <div className="space-y-1.5">
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider ml-1">To</p>
                  <Input
                    type="date"
                    value={filterToDate.split("T")[0]}
                    onChange={(e) => setFilterToDate(e.target.value)}
                    className="w-full h-9"
                  />
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {!listDataLoaded && !isLoading && (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground text-sm">
            Click <strong className="text-foreground">Refresh</strong> in the filters bar to load service orders and
            populate customer and engineer filters.
          </CardContent>
        </Card>
      )}

      {/* Orders List */}
      {listDataLoaded && (
      <div className="space-y-4">
        {filteredOrders.map((order, index) => (
          <Card key={order.id}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-3">
                <div className="min-w-0">
                  <p className="text-sm text-muted-foreground mb-1">
                    #{index + 1} | {order.serviceOrderNumber}
                  </p>
                  <h3 className="text-lg font-semibold truncate">{order.title}</h3>
                </div>
                <div className="flex space-x-2">
                  <Badge className={getPriorityColor(order.priority)}>
                    {order.priority}
                  </Badge>
                  <Badge className={getStatusColor(order.status)}>
                    {order.status.replace("_", " ")}
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 text-sm text-gray-500">
                <p><strong>Customer:</strong> {order.customerName}</p>
                <p><strong>Meter:</strong> {order.meterNumber}</p>
                <p><strong>Engineer:</strong> {order.engineerName}</p>
                <p><strong>Type:</strong> {order.type.replace("_", " ")}</p>
                <p>
                  <strong>Scheduled:</strong>{" "}
                  {order.scheduledDate
                    ? new Date(order.scheduledDate).toLocaleString()
                    : "-"}
                </p>
                <p>
                  <strong>Created:</strong> {new Date(order.createdAt).toLocaleString()}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mt-4">
                {order.status === "pending" && (
                  <Select onValueChange={(value) => assignTechnician(order.id, value)}>
                    <SelectTrigger className="w-full sm:w-56">
                      <SelectValue placeholder="Assign Field Engineer" /> 
                    </SelectTrigger>
                    <SelectContent>
                      {engineerOptions.map((tech) => (
                        <SelectItem key={tech.id} value={tech.id}>
                          {tech.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                <Select
                  value={order.status}
                  onValueChange={(value) => updateOrderStatus(order.id, value)}
                >
                  <SelectTrigger className="w-full sm:w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="assigned">Assigned</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      )}

      {!hasLoadFailed && filteredOrders.length > 0 && (
        <div className="flex justify-center">
          <Button
            type="button"
            variant="outline"
            onClick={handleLoadMore}
            disabled={!hasMoreOrders || isLoadingMore || isLoading}
          >
            {isLoadingMore ? "Loading..." : hasMoreOrders ? "Load More" : "No More Orders"}
          </Button>
        </div>
      )}

      {isLoading && (
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-gray-500">Loading service orders...</p>
          </CardContent>
        </Card>
      )}

      {hasLoadFailed && (
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-gray-500">
              No service orders found matching your criteria.
            </p>
          </CardContent>
        </Card>
      )}

      {listDataLoaded && !hasLoadFailed && !isLoading && orders.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center text-gray-500">
            No service orders were returned. Try Refresh again later.
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ServiceOrderList;
